import { autoinject } from "aurelia-framework";
import { ColDef, ColGroupDef, GridOptions } from "ag-grid";
import { I18N } from "aurelia-i18n";
import { AdminService } from "./admin-service";
import { AdminPumpentyp } from "./admin-pumpentyp";
import { Pumpentyp } from "../productfinder/productfinder-data";
import { ProductfinderService } from "../productfinder/productfinder-service";
import { HttpClientService } from "../../services/http-client-service";
import { KGridRendererFactory } from "../../components/k-grid-renderer-factory";
import { LocaleService } from "../../services/locale-service";
import { DialogFactory } from "../../services/dialog-factory";

@autoinject
export class Pumpentypen {
  pumpentyp?: Pumpentyp;
  adminPumpentyp?: AdminPumpentyp;
  foerderstromDruckstufe?: number;
  foerderstromGridOptions: GridOptions = {
    enableColResize: true,
  };
  drehmomentDruckstufe?: number;
  drehmomentGridOptions: GridOptions = {
    enableColResize: true,
  };
  grenzwerteGridOptions: GridOptions = {
    enableColResize: true,
  };

  constructor(
    private adminService: AdminService,
    private productfinderService: ProductfinderService,
    private httpClientService: HttpClientService,
    private i18n: I18N,
    private localeService: LocaleService,
    private dialogFactory: DialogFactory,
    private gridRendererFactory: KGridRendererFactory,
  ) {}

  activate() {}

  getPumpentypen(search: string): Promise<Pumpentyp[]> {
    return this.productfinderService.getPumpen(search, null);
  }

  async pumpentypChanged(newPumpentyp: Pumpentyp) {
    if (!newPumpentyp) {
      this.adminPumpentyp = undefined;
      this.pumpentyp = undefined;
      return;
    }
    const adminPumpentyp = await this.adminService.getAdminPumpentyp(
      newPumpentyp.id as number,
    );
    this.adminPumpentyp = adminPumpentyp;
    if (adminPumpentyp.foerderstromDruckstufen.length > 0) {
      this.foerderstromDruckstufe = adminPumpentyp.foerderstromDruckstufen[0];
      this.onFoerderstromGridReady();
    } else {
      this.foerderstromDruckstufe = undefined;
    }
    if (adminPumpentyp.drehmomentDruckstufen.length > 0) {
      this.drehmomentDruckstufe = adminPumpentyp.drehmomentDruckstufen[0];
      this.onDrehmomentGridReady();
    } else {
      this.drehmomentDruckstufe = undefined;
    }
    this.onGrenzwerteGridReady();
  }

  async deleteCurrentPumpentyp() {
    if (!this.adminPumpentyp) return;
    const message = this.i18n.tr("admin.pumpentypen.action.reallyDelete", {
      pumpentyp: this.adminPumpentyp.name,
    });
    const confirmed = await this.dialogFactory.openOkCancel(message);
    if (confirmed) {
      await this.adminService.deletePumpentyp(this.adminPumpentyp.id);
      this.adminPumpentyp = undefined;
      this.pumpentyp = undefined;
    }
  }

  async onFoerderstromGridReady() {
    if (!this.adminPumpentyp) return;
    if (this.foerderstromDruckstufe == null) return;
    if (!this.foerderstromGridOptions.api) return;
    const r = await this.httpClientService
      .get()
      .get(
        `admin/pumpentyp/${this.adminPumpentyp.id}/foerderstromverluste?druckstufe=${this.foerderstromDruckstufe}`,
      );
    this.foerderstromGridOptions.api!.setColumnDefs(
      this.createVerlusteColumnDefs(this.adminPumpentyp),
    );
    this.foerderstromGridOptions.api!.setRowData(r.content.result);
    this.foerderstromGridOptions.api!.sizeColumnsToFit();
  }

  async onDrehmomentGridReady() {
    if (!this.adminPumpentyp) return;
    if (this.drehmomentDruckstufe == null) return;
    if (!this.drehmomentGridOptions.api) return;
    const r = await this.httpClientService
      .get()
      .get(
        `admin/pumpentyp/${this.adminPumpentyp.id}/drehmomentverluste?druckstufe=${this.drehmomentDruckstufe}`,
      );
    this.drehmomentGridOptions.api!.setColumnDefs(
      this.createVerlusteColumnDefs(this.adminPumpentyp),
    );
    this.drehmomentGridOptions.api!.setRowData(r.content.result);
    this.drehmomentGridOptions.api!.sizeColumnsToFit();
  }

  async onGrenzwerteGridReady() {
    if (!this.adminPumpentyp) return;
    if (!this.grenzwerteGridOptions.api) return;
    const r = await this.httpClientService
      .get()
      .get(`admin/pumpentyp/${this.adminPumpentyp.id}/grenzwerte`);
    this.grenzwerteGridOptions.api!.setColumnDefs(
      this.createGrenzwerteColumnDefs(),
    );
    this.grenzwerteGridOptions.api!.setRowData(r.content.result);
    this.grenzwerteGridOptions.api!.sizeColumnsToFit();
  }

  private createVerlusteColumnDefs(
    adminPumpentyp: AdminPumpentyp,
  ): (ColDef | ColGroupDef)[] {
    return [
      {
        field: "drehzahl",
        headerName: this.i18n.tr("admin.pumpentypen.table.column.drehzahl"),
        valueFormatter: this.gridRendererFactory.intFormatter(),
        cellClass: "number",
      } as ColDef,
      {
        headerName: this.i18n.tr("admin.pumpentypen.table.column.viskositaet"),
        marryChildren: true,
        children: adminPumpentyp.viskositaetsMessstufen.map((v) => {
          return {
            field: v.toString().replace(".", "_"),
            headerName: this.localeService.formatFloat(v, "#G###D###"),
            valueFormatter:
              this.gridRendererFactory.floatFormatter("#G###D0###"),
            cellClass: "number",
          } as ColDef;
        }),
      } as ColGroupDef,
    ];
  }

  private createGrenzwerteColumnDefs(): ColDef[] {
    return [
      {
        field: "drehzahl",
        headerName: this.i18n.tr("admin.pumpentypen.table.column.drehzahl"),
        valueFormatter: this.gridRendererFactory.intFormatter(),
        cellClass: "number",
      } as ColDef,
      {
        field: "viskositaet",
        headerName: this.i18n.tr("admin.pumpentypen.table.column.viskositaet"),
        valueFormatter: this.gridRendererFactory.floatFormatter("#G###D###"),
        cellClass: "number",
      } as ColDef,
      {
        field: "druck",
        headerName: this.i18n.tr("admin.pumpentypen.table.column.druck"),
        valueFormatter: this.gridRendererFactory.floatFormatter("#D#"),
        cellClass: "number",
      } as ColDef,
    ];
  }
}
